import {
  DEFAULT_GLOBAL_SHORTCUTS,
  formatShortcut,
  type Platform as System,
} from '@lyrmo/shared/shortcuts';
import type { ReactNode } from 'react';
import { Icon } from '../../components/Icon';
import { useClipboard } from '../../hooks/useClipboard';
import { useI18n } from '../../i18n/i18n-context';
import { rich } from '../../i18n/rich';
import type { DesktopPlatform } from './systems';

// Run from a new terminal, where the browser's downloads usually are.
const LINUX_COMMAND = 'chmod +x ~/Downloads/Lyrmo-*.AppImage';

/** The three things to do after downloading: install, let it start, then use its shortcut. */
export function SetupSteps({ platform }: { readonly platform: DesktopPlatform }) {
  const { t } = useI18n();
  const { system } = platform;
  const first: Record<typeof system, readonly ReactNode[]> = {
    mac: [t.downloads.macInstall, t.downloads.macAccess],
    windows: [t.downloads.windowsInstall, t.downloads.windowsOpen],
    linux: [
      <>
        {t.downloads.linuxInstall}
        <Command text={LINUX_COMMAND} />
      </>,
      t.downloads.linuxSession,
    ],
  };
  return (
    <Steps
      steps={[
        ...first[system],
        rich(t.downloads.shortcut, { keys: <ShortcutKeys system={system} /> }),
      ]}
    />
  );
}

function Steps({ steps }: { readonly steps: readonly ReactNode[] }) {
  const { t } = useI18n();
  return (
    <div className="download-steps">
      <p className="download-steps-title">{t.downloads.setup}</p>
      <ol className="download-steps-list">
        {steps.map((step, index) => (
          <li className="download-step" key={index}>
            <span className="download-step-number" aria-hidden="true">
              {index + 1}
            </span>
            <div className="download-step-text">{step}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** The shortcut that rewrites selected text, one keycap for each key. */
function ShortcutKeys({ system }: { readonly system: System }) {
  const shortcut = formatShortcut(DEFAULT_GLOBAL_SHORTCUTS[system].rewrite, system);
  // ⌃⇧W on a Mac, Alt+Shift+W elsewhere.
  const keys =
    system === 'mac'
      ? Array.from(new Intl.Segmenter().segment(shortcut), ({ segment }) => segment)
      : shortcut.split('+');
  return (
    <span className="download-step-keys" dir="ltr">
      {keys.map((key) => (
        <kbd className="kbd download-step-key" key={key}>
          {key}
        </kbd>
      ))}
    </span>
  );
}

function Command({ text }: { readonly text: string }) {
  const { t } = useI18n();
  const { status, copy } = useClipboard<'command'>(1800);
  const copied = status?.outcome === 'copied';
  const label = copied ? t.downloads.copied : t.downloads.copyCommand;
  return (
    <span className="download-command" dir="ltr">
      <code>{text}</code>
      <button
        type="button"
        className="download-command-copy"
        aria-label={label}
        title={label}
        data-copied={copied || undefined}
        onClick={() => void copy('command', text)}
      >
        <Icon name={copied ? 'check' : 'copy'} />
      </button>
    </span>
  );
}
